<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-graduation-cap"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Training Links</div>
          <div class="panel-header__sub">Training resources and guides for your team</div>
        </div>
      </div>

      <!-- Body -->
      <div class="panel-body">

        <div class="body-loading" v-if="loading">
          <div class="body-loading__spinner"></div>
          <span>Loading content…</span>
        </div>

        <div class="content-area" v-else-if="link">
          <div v-html="link"></div>
        </div>

        <div class="empty-state" v-else>
          <i class="fa fa-graduation-cap empty-state__icon"></i>
          <div class="empty-state__title">No training content yet</div>
          <div class="empty-state__sub">Training resources will appear here once added</div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import { FETCH_TRAINING_LINK } from "@/core/services/store/actions.type";

export default {
  data() {
    return {
      loading: true,
      link: "",
      errors: [],
      breadCrumbs: [
        { id: 1, title: "Dashboard",       route: "client.dashboard" },
        { id: 2, title: "Training Links",  route: "" }
      ]
    };
  },
  computed: {
    ...mapGetters(["currentUser"])
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.$store.dispatch(FETCH_TRAINING_LINK).then(data => {
      this.link    = data.data.link || "";
      this.loading = false;
    }).catch(() => { this.loading = false; });
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$border: #dde2ec;

@keyframes spin { to { transform: rotate(360deg); } }

.page-wrap {
  padding: 12px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text { flex: 1; }
  &__title { font-size: 16px; font-weight: 700; color: $dark; line-height: 1.2; }
  &__sub   { font-size: 12px; color: #7a8499; margin-top: 3px; }
}

/* ── body ── */
.panel-body {
  padding: 28px 32px;
}

/* ── loading ── */
.body-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 48px 0;
  font-size: 13px;
  color: #7a8499;

  &__spinner {
    width: 18px;
    height: 18px;
    border: 2.5px solid #c5d8f5;
    border-top-color: $blue;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }
}

/* ── rich-text content ── */
.content-area {
  font-size: 14px;
  line-height: 1.75;
  color: $dark;

  :deep(h1), :deep(h2), :deep(h3), :deep(h4) {
    color: $dark;
    font-weight: 700;
    margin: 1.4em 0 0.5em;
    line-height: 1.3;
  }

  :deep(h1) { font-size: 22px; }
  :deep(h2) { font-size: 18px; }
  :deep(h3) { font-size: 15px; }

  :deep(p) { margin: 0 0 1em; }

  :deep(a) {
    color: $blue;
    text-decoration: none;
    border-bottom: 1px solid rgba(56,154,212,0.35);
    transition: border-color 0.15s, color 0.15s;
    &:hover { color: #2277aa; border-color: #2277aa; }
  }

  :deep(ul), :deep(ol) {
    padding-left: 1.6em;
    margin: 0 0 1em;
    li { margin-bottom: 0.35em; }
  }

  :deep(blockquote) {
    border-left: 3px solid $blue;
    margin: 1.2em 0;
    padding: 10px 16px;
    background: rgba(56,154,212,0.05);
    border-radius: 0 8px 8px 0;
    color: #5a6278;
    font-style: italic;
  }

  :deep(code) {
    background: #f0f3fa;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 12px;
    font-family: 'Courier New', monospace;
    color: #2d6a9f;
  }

  :deep(pre) {
    background: #f0f3fa;
    border-radius: 8px;
    padding: 14px 16px;
    overflow-x: auto;
    font-size: 12px;
    margin: 0 0 1em;
  }

  :deep(img) {
    max-width: 100%;
    border-radius: 8px;
    margin: 8px 0;
  }

  :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
    font-size: 13px;
    th, td {
      padding: 9px 14px;
      border: 1px solid $border;
      text-align: left;
    }
    th { background: #f4f6fb; font-weight: 600; color: $dark; }
    tr:nth-child(even) td { background: #fafbfd; }
  }
}

/* ── empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 10px;

  &__icon  { font-size: 38px; color: #c5d0e0; }
  &__title { font-size: 15px; font-weight: 600; color: $dark; }
  &__sub   { font-size: 13px; color: #7a8499; }
}
</style>
